import React from 'react';
import { FmButton } from './FmButton';
import { FmField } from './FmField';
import { FmFormFlowEditableCell } from './FmFormFlowEditableCell';
import { FmInline, FmStack, FmText } from './FmComposition';
import { FmEmpty } from './FmFeedback';
import type {
  FmFormFlowBlockDescriptor,
  FmFormFlowControlDescriptor,
  FmFormFlowEvent,
  FmFormFlowPageDescriptor,
} from './FmFormFlowPage.types';

function emit(descriptor: FmFormFlowPageDescriptor, event: FmFormFlowEvent): void {
  descriptor.onEvent?.(event);
}

export function FmFormFlowEditableCollection({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const control = block.control as Extract<
    FmFormFlowControlDescriptor,
    { type: 'collection'; mode: 'edit' }
  >;
  const atMax = control.maxRows !== undefined && control.rows.length >= control.maxRows;
  const atMin = control.minRows !== undefined && control.rows.length <= control.minRows;
  return (
    <FmStack gap="sm">
      {control.rows.length ? (
        <ul className="fm-form-flow__collection">
          {control.rows.map((row, rowIndex) => (
            <li className="fm-form-flow__collection-row" key={row.id}>
              <div className="fm-form-flow__collection-cells">
                {control.columns.map((column) => (
                  <FmField
                    key={column.id}
                    label={column.label}
                    htmlFor={`${block.id}-${row.id}-${column.id}`}
                  >
                    <FmStack gap="xs">
                      <FmFormFlowEditableCell
                        id={`${block.id}-${row.id}-${column.id}`}
                        ariaLabel={`${column.label} ${rowIndex + 1}`}
                        testId={`form-flow-cell-${block.id}-${row.id}-${column.id}`}
                        editor={column.editor}
                        value={row.cells[column.id]}
                        onChange={(value) =>
                          emit(descriptor, {
                            kind: 'collection-change',
                            blockId: block.id,
                            rowId: row.id,
                            columnId: column.id,
                            value,
                          })
                        }
                      />
                      {column.valueLabels?.[String(row.cells[column.id] ?? '')] ? (
                        <FmText size="caption" tone="muted">
                          {column.valueLabels[String(row.cells[column.id] ?? '')]}
                        </FmText>
                      ) : null}
                      {rowIndex === 0 && column.helper ? (
                        <FmText size="caption" tone="muted">
                          {column.helper}
                        </FmText>
                      ) : null}
                    </FmStack>
                  </FmField>
                ))}
              </div>
              {/* The row's own action rail, in the DS row-action treatment: a
                  destructive control aligned to the row it acts on, at the
                  governed tap target. It used to be a grey text label flowing as
                  one more auto-fit column, so it detached from its row and
                  aligned to nothing. */}
              <div className="fm-form-flow__collection-row-actions">
                <FmButton
                  variant="text"
                  size="sm"
                  danger
                  disabled={atMin}
                  aria-label={`${control.removeLabel} ${rowIndex + 1}`}
                  onClick={() =>
                    emit(descriptor, {
                      kind: 'collection-remove',
                      blockId: block.id,
                      rowId: row.id,
                    })
                  }
                >
                  {control.removeLabel}
                </FmButton>
              </div>
              {row.validation?.status === 'invalid' ? (
                <FmText role="alert" tone="negative" size="caption">
                  {row.validation.message}
                </FmText>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <FmEmpty description={control.emptyLabel} />
      )}
      <FmInline>
        <FmButton
          variant="bordered"
          disabled={atMax}
          onClick={() => emit(descriptor, { kind: 'collection-add', blockId: block.id })}
        >
          {control.addLabel}
        </FmButton>
      </FmInline>
    </FmStack>
  );
}
